import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Formik } from 'formik';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { SignUpOTPSchema } from '../../validation/login/login-validation';
import { Common } from '../../utils/constants';
import { getAPIKey, login, signInConfirm } from '../../services/login/login-service';
import images from '../../assets/images';
import { companyNameReplace } from '../../utils';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch, store } from '../../store';
import {
  changeLanguage,
  logout,
  saveUserData,
  setCurrencyDetails,
  userDetail as setUserDetail,
} from '../../store/actions/app.Actions';
import type { ISignInOTPProps } from '../../types/propTypes';
import type { StandardResponse } from '../../models/standard-response';
import type { IUserData } from '../../models/profile';
import type { ISignInOTPFormField } from '../../models/login';
import OTPField from '../../components/common/OTPField';
import { useCountdownTimer } from '../../utils/useCountdownTimer';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import RecaptchaV2Net from '../../components/RecaptchaV2Net';
import { getAuthOptions, resetAuthSetup } from '../../services/auth-setup/auth-setup-service';
import { maskEmail } from '../../utils/helper';

const SignInOTPForm = (props: ISignInOTPProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [useV2, setUseV2] = useState(false);

  const [siteKey, setSiteKey] = useState<string>('');
  const [otpValidationTime, setOTPValidationTime] = useState<string>('');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const [captchaSolved, setCaptchaSolved] = useState<boolean>(false);
  const [captchaError, setCaptchaError] = useState<boolean>(false);
  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  const initialState = {
    code: '',
  };

  useEffect(() => {
    getOTPValidationTime();
  }, []);

  const { secondsLeft, start, formattedTime, isRunning } = useCountdownTimer({
    initialSeconds: 45,
    autoStart: true,
  });

  useEffect(() => {
    if (useV2) {
      getRecaptchaSiteKey();
      setCaptchaSolved(false);
      setCaptchaError(true);
    }
  }, [useV2]);

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    )?.value;
    setSiteKey(siteKey ?? '');
  };

  const handleSubmit = async (value: ISignInOTPFormField) => {
    const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';

    sessionStorage.setItem(Common.OTP_VERIFICATION_IN_PROGRESS, 'true');
    setIsSubmittingOTP(true);
    document.getElementById('loading')?.classList.add('manual-loading');

    return await signInConfirm(value)
      .then(async (res) => {
        if (res && res.isSignedIn && res.nextStep.signInStep === Common.AuthenticationSteps.DONE) {
          const authSession = await fetchAuthSession();
          const authResponse = { ...res, authSession };

          await getProfile()
            .then(async (userDetail: StandardResponse<IUserData>) => {
              if (
                userDetail &&
                userDetail.Data &&
                userDetail.Data.status &&
                userDetail.Data.status.code === Common.UserStatus.CANCELLED
              ) {
                dispatch(logout()).then(() => {
                  toast.error(t('login.toast.cancelled'), { autoClose: 15000 });
                });
              } else {
                dispatch(saveUserData({ ...authResponse, ...userDetail.Data }));
                dispatch(changeLanguage(userDetail?.Data?.language_preference));
                dispatch(setCurrencyDetails(userDetail?.Data?.default_currency));
                dispatch(setUserDetail(userDetail?.Data));
                moment.locale(store.getState().UserData?.language?.code);

                if (localStorage.getItem(Common.SHOW_UPDATE_MFA_MODAL) === 'true') {
                  await resetAuthSetup(userDetail.Data.email);
                }

                const response = await getAuthOptions();
                const isMFARequired = response.Data.driver2FAStatus.every((method) => !method.is_enabled);
                if (isMFARequired) {
                  sessionStorage.setItem(Common.SIGNUP_FLOW_ACTIVE, 'true');
                  sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
                  navigate('/auth/select-auth-option', { replace: true });
                  return;
                }
                if (downloadVATRedirect) {
                  sessionStorage.removeItem('vat_receipt_path');
                  navigate(downloadVATRedirect);
                  return;
                }
                if (userDetail?.Data.is_profile_verified === false) {
                  navigate(Common.Routes.PROFILE_REDIRECT);
                  return;
                }
                if (userDetail?.Data.payment_type === null) {
                  navigate(Common.Routes.PAYMENT);
                  return;
                }
                navigate(Common.Routes.HOME);
              }
            })
            .catch(() => {
              sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
              dispatch(logout());
              toast.error(t('login.toast.error'));
            });
        } else if (
          res &&
          res.isSignedIn === false &&
          res.nextStep.signInStep === Common.AuthenticationSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
        ) {
          toast.error(t('signup.toast.invalidCode'));
        }
      })
      .catch((error) => {
        sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
        if (error && error.name === Common.Authentication.CodeMismatchException) {
          toast.error(t('signup.toast.invalidCode'));
        } else if (
          error?.name === Common.Authentication.NotAuthorizedException &&
          error?.message?.toLowerCase()?.includes('invalid session')
        ) {
          toast.error(t('signup.toast.expiredCode'));
        } else {
          toast.error(t('signup.toast.error'));
        }
      })
      .finally(() => {
        sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
        setIsSubmittingOTP(false);
        document.getElementById('loading')?.classList.remove('manual-loading');
      });
  };

  const handleRecaptchaChange = async (recaptchaCode: string | null) => {
    if (recaptchaCode) {
      setCaptchaSolved(true);
      setCaptchaError(false);
      await resendOtpSubmit(recaptchaCode);
      recaptchaRef.current?.reset();
      setSiteKey('');
      setUseV2(false);
      start();
    }
  };

  const handleResendClick = async (
    resetForm: () => void,
    setFieldTouched: (field: string, touched: boolean) => void,
  ) => {
    resetForm();
    setFieldTouched('code', false);

    if (!executeRecaptcha) {
      return;
    }
    try {
      const token = await executeRecaptcha('resend_code');
      await resendOtpSubmit(token);
      start();
    } catch {
      setUseV2(true);
      if (useV2 && !captchaSolved) {
        setCaptchaError(true);
        return;
      }
    }
  };

  const resendOtpSubmit = async (recaptchaCode: string) => {
    document.getElementById('loading')?.classList.remove('spinner-disable');
    await login(
      {
        email: props.email,
        password: props.password,
        recaptchaCode,
      },
      useV2,
      false,
    )
      .then(() => {
        toast.success(t('signup.toast.resendSignupCode'));
        if (useV2) {
          setUseV2(false);
        }
      })
      .catch(() => {
        if (!useV2) {
          setUseV2(true);
          return;
        }
        toast.error(t('signup.toast.error'));
      })
      .finally(() => {
        document.getElementById('loading')?.classList.add('spinner-disable');
      });
  };

  const getOTPValidationTime = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.LOGIN_VERIFICATION_CODE_EXPIRY,
    });
    setOTPValidationTime(config.data[0]?.value);
  };

  return (
    <div className="font-poppins">
      <Helmet>
        <title>{companyNameReplace(t('login.title'))} </title>
      </Helmet>

      {!props.isResetAuthMethod && (
        <button
          type="button"
          className="btn btn-icon btn-link pr-0 btn-back"
          onClick={() => props.onChangeStage(Common.AuthStage.SIGNIN)}
          title={t<string>('signup.button.back')}
        >
          <img className="icon" src={images.backArrowRSVG} alt={t<string>('signup.button.back')} />
        </button>
      )}

      <div className="title-outer">
        <div className="d-flex justify-content-center align-items-center">
          <div className="sign-up-icon-container">
            <img className="sign-up-icon" src={images.OTPEmailIcon} alt={t<string>('navbar.title')} />
          </div>
        </div>
        <h1 className="title sign-up-title mt-5 mb-3">{t('signup.label.signUpOTPConfirmationTitle')}</h1>

        <span className="sign-up-otp-message">{t('signup.label.signUpOTPConfirmationMessage')}</span>
        <p className="sign-up-otp-message">{maskEmail(props.email)}</p>
        <p className="signin-otp-validation-time mb-1">
          {t('signup.label.otpValidationTimeTitle').replace('##VALIDATIONTIME##', otpValidationTime)}
        </p>
      </div>

      <Formik initialValues={initialState} onSubmit={handleSubmit} validationSchema={SignUpOTPSchema(t)}>
        {(props) => {
          const { handleSubmit, resetForm, setFieldTouched } = props;
          return (
            <form
              className="form-secondary"
              onSubmit={handleSubmit}
              autoComplete="off"
              noValidate
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isSubmittingOTP) {
                  event.preventDefault();
                }
              }}
            >
              <div className="app-setup-otp-container p-1 mb-3">
                <OTPField name="code" customStyleClass="app-setup-otp" />
              </div>

              <Form.Group className="form-group mb-5 mt-4">
                <Button
                  type="submit"
                  className="btn btn-primary w-100 sign-up-otp-submit"
                  disabled={sessionStorage.getItem(Common.OTP_VERIFICATION_IN_PROGRESS) === 'true' || isSubmittingOTP}
                >
                  {t('signup.button.confirm')}
                </Button>
              </Form.Group>
              {isRunning && secondsLeft > 0 ? (
                <div className="clearfix pt-2">
                  <p className="sign-up-otp pb-0">{t('signup.label.otpSentMessage')}</p>
                  <p className="sign-up-otp">
                    <span>
                      {t('signup.label.resendMessage')} {formattedTime}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="clearfix pt-1">
                  <p className="sign-up-otp pb-0 mb-2">{t('signup.label.resendTitle')} </p>
                  <p className="sign-up-otp">
                    <a
                      href="#"
                      title={t<string>('signup.button.resend')}
                      onClick={(event) => {
                        event.preventDefault();
                        handleResendClick(resetForm, setFieldTouched);
                      }}
                    >
                      {t('signup.button.resend')}
                    </a>
                  </p>
                </div>
              )}
              {useV2 && siteKey && (
                <div className="mt-3 d-flex justify-content-center">
                  <RecaptchaV2Net
                    ref={recaptchaRef}
                    sitekey={siteKey}
                    onChange={handleRecaptchaChange}
                    hl={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                    key={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                  />
                </div>
              )}
              {captchaError && (
                <p className="text-center mt-2" style={{ color: '#d73a1f', fontSize: '12px' }}>
                  {t('signup.toast.verifyRecaptchaToResend')}
                </p>
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SignInOTPForm;
